import React from 'react';
import { Link } from 'react-router-dom';
import { LandingNav } from '../components/LandingNav';
import { LandingFooter } from '../components/LandingFooter';
import './termsOfService.css';

export const TermsOfService: React.FC = () => {
    const lastUpdated = new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <div className="raabta-landing">
            <LandingNav showMenu={false} />

            <main style={{ padding: '2rem 4rem' }}>
                <header className="tos-header">
                    <h1 className="tos-title">Terms of Service</h1>
                    <p className="tos-subtitle">
                        Please read these Terms of Service carefully before using the Raabta platform.
                    </p>
                    <div className="tos-meta">
                        Last updated: {lastUpdated} · Version 1.0
                    </div>
                </header>

                <article className="tos-article">
                    <section className="tos-sec">
                        <h2>1. Acceptance of Terms</h2>
                        <p>
                            Raabta ("we", "our", "us") provides a school communication and ticketing platform
                            that helps schools, parents, employees, and administrators connect and resolve issues.
                            By accessing or using the Raabta web application, mobile applications, or related services,
                            you agree to be bound by these Terms of Service ("Terms").
                        </p>
                        <p>
                            If you are agreeing to these Terms on behalf of a school, district, or other legal entity,
                            you represent that you have the authority to bind that entity to these Terms. If you do not
                            agree, you must not access or use the platform.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>2. Account Registration & Security</h2>
                        <p>To use Raabta, you must register for an account. You agree to:</p>
                        <ul>
                            <li>Provide accurate, current, and complete registration information.</li>
                            <li>Maintain the confidentiality and security of your login credentials (username and password).</li>
                            <li>Promptly update your information if there are any changes.</li>
                            <li>Immediately notify us or your school administrator of any unauthorized account use or security breach.</li>
                        </ul>
                        <p>
                            Administrators are responsible for all activity occurring under their school tenant account, including
                            managing employee and parent role assignments.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>3. Platform Role-Based Usage</h2>
                        <p>
                            Raabta provides role-based features designed specifically for:
                        </p>
                        <ul>
                            <li><strong>Super Admins</strong>: Managing school tenants, platform settings, and system-wide configurations.</li>
                            <li><strong>School Admins</strong>: Configuring departments, setting up employee and parent directories, and designing ticketing workflows.</li>
                            <li><strong>Employees / Staff</strong>: Managing ticket queues, responding to parent queries, and updating ticket status.</li>
                            <li><strong>Parents</strong>: Raising tickets, viewing communication history, and querying the AI assistant.</li>
                        </ul>
                        <p>
                            You agree to only access resources and use features corresponding to your assigned role.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>4. Permitted Use & Conduct</h2>
                        <p>
                            You agree to use Raabta solely for authorized school-related communications, inquiries, and ticket resolution.
                            You must not:
                        </p>
                        <ul>
                            <li>Use the platform to harass, abuse, threaten, or defame school staff, parents, or students.</li>
                            <li>Upload or distribute files containing malware, viruses, or any code intended to damage or intercept system data.</li>
                            <li>Attempt to bypass, test, or breach any security measures or access restrictions.</li>
                            <li>Attempt to access data belonging to another school or tenant (violating multi-tenant isolation).</li>
                            <li>Use automated tools (bots, scrapers) to access the platform without our written consent.</li>
                        </ul>
                    </section>

                    <section className="tos-sec">
                        <h2>5. School Content & AI Assistant</h2>
                        <p>
                            Schools may upload documentation, policies, handbooks, and FAQ materials to the **Knowledge Hub**
                            to power the AI ticketing assistant.
                        </p>
                        <p className="tos-emphasis">
                            <strong>Schools retain all rights and ownership of their uploaded content.</strong>
                        </p>
                        <p>
                            By uploading materials, you grant Raabta a worldwide, non-exclusive, royalty-free license to host, process, and use the content
                            strictly to provide service functionalities, including training local AI models for your school instance.
                            The school represents and warrants that it has all necessary rights to upload such content and that doing so
                            does not violate any third-party intellectual property or privacy laws.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>6. Billing, Subscriptions, & Refunds</h2>
                        <p>
                            Schools subscribing to paid plans (Pro or Enterprise) agree to the pricing and billing cycles outlined at signup:
                        </p>
                        <ul>
                            <li>Subscription fees are billed in advance on a recurring monthly or annual basis.</li>
                            <li>Payments are non-refundable except as explicitly required by law or agreed in a custom Service Level Agreement (SLA).</li>
                            <li>If a subscription is canceled, access to paid features will continue until the end of the current billing cycle.</li>
                            <li>Failure to pay subscription fees may result in account suspension and data loss.</li>
                        </ul>
                    </section>

                    <section className="tos-sec">
                        <h2>7. Data Isolation & Privacy</h2>
                        <p>
                            Raabta is a multi-tenant platform. Data within a school instance is strictly isolated and is only visible
                            to authorized members of that school.
                        </p>
                        <p>
                            Our collection and use of personal data are governed by our <Link to="/privacy-policy">Privacy Policy</Link>.
                            By using Raabta, you consent to the data collection and processing activities described therein.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>8. Suspension & Termination</h2>
                        <p>
                            We reserve the right to suspend or terminate your account or school tenant access at any time for violation of these Terms,
                            non-payment, or if we believe it is necessary to protect the platform's security.
                        </p>
                        <p>
                            Upon tenant termination, we will delete or anonymize all associated school data, parent directories, and ticket archives
                            within <strong>30 days</strong>, subject to legal retention obligations.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>9. Disclaimer of Warranties</h2>
                        <p>
                            Raabta is provided on an "as is" and "as available" basis, without warranties of any kind, either express or implied,
                            including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.
                        </p>
                        <p>
                            We do not warrant that the platform will be uninterrupted, secure, error-free, or that any answers generated by the
                            AI assistant will be 100% accurate. Users should verify critical school policies independently.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>10. Limitation of Liability</h2>
                        <p>
                            To the maximum extent permitted by law, Raabta shall not be liable for any indirect, incidental, special,
                            consequential, or punitive damages, or any loss of profits, data, or goodwill, arising out of or related to your use of the platform.
                        </p>
                        <p>
                            In no event shall our total liability for all claims exceed the amount paid by your school to Raabta during the
                            twelve (12) months preceding the event giving rise to the liability.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>11. Governing Law</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which our
                            company is registered, without regard to its conflict of law principles. Any legal actions arising under these Terms
                            must be filed in the courts of that jurisdiction.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>12. Changes to Terms</h2>
                        <p>
                            We may update these Terms of Service from time to time. We will notify you of material changes by posting
                            the updated Terms on our website and updating the "Last updated" date. Continued use of the platform after updates
                            take effect constitutes acceptance of the new Terms.
                        </p>
                    </section>

                    <section className="tos-sec">
                        <h2>13. Contact Us</h2>
                        <p>
                            If you have questions about these Terms of Service, please contact our legal and support team at:
                        </p>
                        <p className="tos-contact">
                            <strong>support@raabta.app</strong>
                            <br />
                            <span className="tos-contact-sub">We aim to respond within 2 business days.</span>
                        </p>
                    </section>
                </article>
            </main>

            <LandingFooter />
        </div>
    );
};
